import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	FormSectionTitle,
	NextStepCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { FormPageMode } from '@practium/ui';
import type {
	PropertiesTab,
	PropertyPageMode,
	PropertyFormData,
	SectorFormData,
	AreaFormData,
} from '../types';
import { PropertyForm } from '../components/Forms/PropertyForm/PropertyForm';
import type { PropertyFormRef } from '../components/Forms/PropertyForm/PropertyForm';
import { SectorForm } from '../components/Forms/SectorForm/SectorForm';
import type { SectorFormRef } from '../components/Forms/SectorForm/SectorForm';
import { AreaForm } from '../components/Forms/AreaForm/AreaForm';
import type { AreaFormRef } from '../components/Forms/AreaForm/AreaForm';
import { PropertyPhotoCard } from '../components/PropertyPhotoCard/PropertyPhotoCard';
import {
	NEXT_STEP_CONTENT,
	PROPERTY_TITLES,
	PROPERTY_SUBTITLES,
	PROPERTY_SECTION_TITLES,
	PROPERTY_CARD_TITLES,
} from '../constants/page.constants';
import styles from './AddPropertyPage.module.css';
import { useGetAreas, useGetPropertyById } from '@practium/features/api';

export interface AddPropertyPageProps {
	type: PropertiesTab;
	onBack: () => void;
	initialMode?: PropertyPageMode;
	initialData?: PropertyFormData | SectorFormData | AreaFormData;
	onDelete?: () => void | Promise<void>;
	onIaAnalysis?: () => void;
	avatarUrl?: string | undefined;
	avatar?: File | null;
	propId?: string;
	canDelete?: boolean;
	canEdit?: boolean;
	canCreate?: boolean;
	onGoToList?: () => void;
	onCreateNext?: (nextType: 'areas' | 'sectors', propId: string) => void;
	onCreated?: (id: string) => void;
	childCount?: number;
}

export const AddPropertyPage = ({
	type,
	onBack,
	initialMode = 'add',
	initialData,
	onDelete,
	onIaAnalysis,
	avatarUrl,
	propId,
	canDelete = true,
	canEdit = true,
	canCreate = true,
	onGoToList,
	onCreateNext,
	onCreated,
	childCount,
}: AddPropertyPageProps) => {
	const [currentMode, setCurrentMode] = useState<PropertyPageMode>(initialMode);
	const [isLoading, setIsLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [showNextStep, setShowNextStep] = useState(false);
	const [createdPropertyId, setCreatedPropertyId] = useState<string | undefined>();

	const propertyRef = useRef<PropertyFormRef>(null);
	const sectorRef = useRef<SectorFormRef>(null);
	const areaRef = useRef<AreaFormRef>(null);

	const { data: allAreas } = useGetAreas({ enabled: type === 'sectors' });
	const sectorAvailableAreas = useMemo(
		() => (propId ? allAreas.filter((a) => a.propId === propId) : allAreas),
		[allAreas, propId]
	);

	const { data: contextProperty } = useGetPropertyById(propId ?? '', {
		enabled: !!propId && type !== 'properties',
	});
	const propertyName = contextProperty?.formData.name ?? '';

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess, error: toastError } = useToast();

	const handleFormSubmit = useCallback(
		(createdId?: string) => {
			if (currentMode !== 'add') {
				onBack();
				return;
			}
			if (createdId) {
				if (type === 'properties') setCreatedPropertyId(createdId);
				onCreated?.(createdId);
			}
			setCurrentMode('view');
			setShowNextStep(true);
		},
		[currentMode, onBack, onCreated, type]
	);

	const isFormDirty = useCallback(() => {
		if (avatarFile) return true;
		if (type === 'properties') return propertyRef.current?.isDirty?.() ?? false;
		if (type === 'areas') return areaRef.current?.isDirty?.() ?? false;
		return sectorRef.current?.isDirty?.() ?? false;
	}, [avatarFile, type]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setAvatarFile(null);
			if (currentMode === 'edit' && initialMode === 'view') {
				setCurrentMode('view');
			} else {
				onBack();
			}
		};

		if (!isFormDirty()) {
			revert();
			return;
		}

		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [confirm, currentMode, initialMode, isFormDirty, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir cadastro',
			message: 'Tem certeza que deseja excluir este cadastro? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			await onDelete?.();
			toastSuccess('Cadastro excluído com sucesso.');
		} catch (err) {
			toastError(err instanceof Error ? err.message : 'Não foi possível excluir o cadastro.');
		}
	}, [confirm, onDelete, toastSuccess, toastError]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		if (type === 'properties') propertyRef.current?.submit();
		else if (type === 'areas') areaRef.current?.submit();
		else sectorRef.current?.submit();
	}, [type]);

	const formMode: FormPageMode = currentMode;

	const title = PROPERTY_TITLES[type][currentMode];
	const subtitle = PROPERTY_SUBTITLES[currentMode][type];
	const sectionTitle = PROPERTY_SECTION_TITLES[type];
	const cardTitle = PROPERTY_CARD_TITLES[type];

	const entityName =
		type === 'properties'
			? ((initialData as PropertyFormData | undefined)?.name ?? undefined)
			: ((initialData as SectorFormData | AreaFormData | undefined)?.name ?? undefined);

	const pageActions = (
		<div className={styles.pageActions}>
			<FormActions
				mode={formMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isLoading}
				onIaAnalysis={type === 'sectors' ? onIaAnalysis : undefined}
				onDelete={onDelete && canDelete ? handleDelete : undefined}
				onEdit={canEdit ? handleEdit : undefined}

			/>
		</div>
	);

	const nextStepPropId = type === 'properties' ? (createdPropertyId ?? propId) : propId;
	const nextStepContent = NEXT_STEP_CONTENT[type];

	const handleNextStepSecondary = useCallback(() => {
		onGoToList?.();
	}, [onGoToList]);

	const handleNextStepPrimary = useCallback(() => {
		if (!nextStepPropId) return;
		onCreateNext?.(nextStepContent.nextType, nextStepPropId);
	}, [onCreateNext, nextStepContent.nextType, nextStepPropId]);

	const missingChildren = type !== 'sectors' && childCount === 0;

	const nextStepCard = canCreate && currentMode === 'view' && (showNextStep || missingChildren) && (
		<NextStepCard
			title={nextStepContent.title}
			subtitle={nextStepContent.subtitle}
			description={nextStepContent.description}
			secondaryLabel="Depois"
			primaryLabel={nextStepContent.primaryLabel}
			onSecondary={handleNextStepSecondary}
			onPrimary={handleNextStepPrimary}
		/>
	);

	const localAvatarUrl = useMemo(
		() => (avatarFile ? URL.createObjectURL(avatarFile) : undefined),
		[avatarFile]
	);

	useEffect(() => {
		if (!localAvatarUrl) return;
		return () => URL.revokeObjectURL(localAvatarUrl);
	}, [localAvatarUrl]);

	const avatarCard = (
		<PropertyPhotoCard
			mode={currentMode}
			file={avatarFile}
			onFileChange={setAvatarFile}
			entityName={entityName}
			avatarUrl={avatarUrl ?? localAvatarUrl}
			onEditRequest={canEdit ? handleEdit : undefined}
		/>
	);

	return (
		<div className={styles.page}>
			<PageHeader title={title} subtitle={subtitle} onBack={onBack} />
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title={cardTitle}
						className={styles.mainCard}
					>
						<div className={styles.formContent}>
							<FormSectionTitle>{sectionTitle}</FormSectionTitle>
							<div className={styles.fields}>
								{type === 'properties' && (
									<PropertyForm
										ref={propertyRef}
										mode={currentMode}
										initialData={initialData as PropertyFormData | undefined}
										onSubmit={handleFormSubmit}
										avatarfile={avatarFile}
										onLoadingChange={setIsLoading}
									/>
								)}
								{type === 'areas' && (
									<AreaForm
										ref={areaRef}
										mode={currentMode}
										propId={propId ?? ''}
										propertyName={propertyName}
										initialData={initialData as AreaFormData | undefined}
										onSubmit={handleFormSubmit}
										onLoadingChange={setIsLoading}
										avatarFile={avatarFile}
									/>
								)}
								{type === 'sectors' && (
									<SectorForm
										ref={sectorRef}
										mode={currentMode}
										initialData={initialData as SectorFormData | undefined}
										availableAreas={sectorAvailableAreas}
										propId={propId}
										propertyName={propertyName}
										avatarfile={avatarFile}
										onSubmit={handleFormSubmit}
										onLoadingChange={setIsLoading}
									/>
								)}
							</div>
						</div>
					</FormCard>
					{pageActions}
					{nextStepCard}
				</div>
				{avatarCard}
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
